import { useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Seo } from '@/components/Seo';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDestinationMonthlyIntelligence } from '@/hooks/useDestinationMonthlyIntelligence';
import SeasonAbilityGraph from '@/components/destination/SeasonAbilityGraph';
import { DEV_MONTHLY_INTELLIGENCE_FIXTURE } from './__fixtures__/monthly-intelligence.dev-fixture';


// Dev-only route for testing the Season x Ability graph against real imported data
// without touching DestinationDetail.tsx. Not linked from anywhere in the site nav.
// Safe to remove once the graph is approved and wired into the real destination page.
//
// IDs are hardcoded rather than looked up by slug because public.destinations has a
// pre-existing, unrelated gap (anon/authenticated lack the base SELECT grant on that
// table locally -- confirmed separately during this work, not introduced by it) that
// this dev-only page has no reason to depend on or fix. These are the same real
// destination IDs preserved by the local fixture migration
// (20260811024038_add_dev_fixture_destinations_for_monthly_intelligence.sql).
const DEV_DESTINATIONS = [
  { id: '30266aed-3be9-464f-836f-5923ea8ae397', slug: 'ericeira', name: 'Ericeira' },
  { id: '44748592-5c19-4207-b75d-ac5f8fa1560b', slug: 'santa-teresa', name: 'Santa Teresa' },
  { id: 'cd135d6e-62a5-4318-9630-20066e79a5be', slug: 'siargao', name: 'Siargao' },
] as const;

const SeasonAbilityGraphDev = () => {
  const [selectedSlug, setSelectedSlug] = useState<string>('ericeira');
  // Explicit, manual switch -- never an automatic fallback. The fixture is only
  // ever used because a human on this dev-only route asked for it.
  const [source, setSource] = useState<'fixture' | 'live'>('fixture');

  const selectedDestination = DEV_DESTINATIONS.find((d) => d.slug === selectedSlug);
  const { data: liveData = [], isLoading: liveLoading } = useDestinationMonthlyIntelligence(
    source === 'live' ? selectedDestination?.id : undefined
  );

  const monthlyData =
    source === 'fixture'
      ? (selectedDestination ? DEV_MONTHLY_INTELLIGENCE_FIXTURE[selectedDestination.slug] ?? [] : [])
      : liveData;
  const monthlyLoading = source === 'live' && liveLoading;


  return (
    <Layout>
      <Seo title="Season x Ability graph (dev)" description="Internal dev route." path="/dev/season-ability" noIndex />
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Internal dev route</p>
          <h1 className="font-serif text-2xl md:text-3xl text-foreground mb-6">Season &amp; Ability graph</h1>

          <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Select destination">
            {DEV_DESTINATIONS.map((d) => (
              <button
                key={d.slug}
                type="button"
                onClick={() => setSelectedSlug(d.slug)}
                className={cn(
                  'min-h-[44px] rounded-full border px-4 text-sm transition-colors',
                  selectedSlug === d.slug
                    ? 'border-primary bg-primary/10 text-foreground font-medium'
                    : 'border-border text-muted-foreground hover:border-primary/50',
                )}
              >
                {d.name}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 text-xs">
            <span className="uppercase tracking-wide text-muted-foreground">Data source</span>
            {(['fixture', 'live'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSource(s)}
                className={cn(
                  'min-h-[36px] border px-3 transition-colors',
                  source === s ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground',
                )}
              >
                {s === 'fixture' ? 'Approved pilot fixture (dev only)' : 'Live database'}
              </button>
            ))}
          </div>


          {!selectedDestination ? (
            <p className="text-sm text-muted-foreground">No destination selected.</p>
          ) : monthlyLoading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <SeasonAbilityGraph monthlyData={monthlyData} />
          )}
        </div>
      </section>
    </Layout>
  );
};

export default SeasonAbilityGraphDev;
